import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/services/api.ts";
import type { AlertSettingsUpdate } from "@/types";

/** The viewed company's notification groups. Only mounted while the panel is open. */
export function useAlertSettings() {
  const { effectiveCompanyId } = useAuth();
  return useQuery({
    queryKey: ["alertSettings", effectiveCompanyId],
    queryFn: () => api.alertSettings.get(),
    enabled: !!effectiveCompanyId,
    retry: false,
  });
}

/**
 * Saves the whole group list. `save` drops a call made while one is in flight: a second PUT would
 * carry the same version and be refused as a conflict. `reset` does nothing while one is in flight,
 * so `isPending` holds for as long as `save` drops calls.
 */
export function useSaveAlertSettings() {
  const { effectiveCompanyId } = useAuth();
  const queryClient = useQueryClient();
  const inFlight = useRef(false);
  const mutation = useMutation({
    mutationFn: (update: AlertSettingsUpdate) => api.alertSettings.put(update),
    // Returned, so the save stays pending until the new version has been read back.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["alertSettings", effectiveCompanyId] }),
    // Config-level, not per-call: the Mutation itself runs this even after reset() has detached
    // the observer, so a reset mid-save can't leave the guard stuck set.
    onSettled: () => {
      inFlight.current = false;
    },
  });

  function save(update: AlertSettingsUpdate, onSaved?: () => void) {
    if (inFlight.current) return;
    inFlight.current = true;
    mutation.mutate(update, { onSuccess: () => onSaved?.() });
  }

  function reset() {
    if (!inFlight.current) mutation.reset();
  }

  return { save, isPending: mutation.isPending, error: mutation.error, reset };
}
